import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { describePasswordViolations, PASSWORD_POLICY_HINT } from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field } from '../components/ui';

export function ChangePasswordPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const violations = useMemo(() => describePasswordViolations(newPassword), [newPassword]);
  const mismatch = confirmation.length > 0 && confirmation !== newPassword;
  const canSubmit =
    currentPassword.length > 0 && newPassword.length > 0 && violations.length === 0 && !mismatch;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFields({});
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      // Trocar a senha derruba as sessões (tokenVersion muda), inclusive esta.
      await logout();
      navigate('/login', {
        replace: true,
        state: { notice: 'Senha alterada. Entre novamente com a nova senha.' },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Erro inesperado. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Alterar senha</h1>
      <p className="mt-1 text-sm text-slate-600">
        Ao salvar, você será desconectado de todos os dispositivos.
      </p>

      <Card className="mt-6">
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <Alert>{error}</Alert>}

          <Field
            label="Senha atual"
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={currentPassword}
            error={fields.currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />

          <Field
            label="Nova senha"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            error={fields.newPassword}
            hint={PASSWORD_POLICY_HINT}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          {newPassword.length > 0 && violations.length > 0 && (
            <ul className="space-y-1">
              {violations.map((problem) => (
                <li key={problem} className="text-xs text-slate-600">
                  • {problem}
                </li>
              ))}
            </ul>
          )}

          <Field
            label="Confirme a nova senha"
            name="confirmation"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            error={mismatch ? 'As senhas não coincidem.' : undefined}
            onChange={(e) => setConfirmation(e.target.value)}
          />

          <div className="flex gap-3 pt-1">
            <Button type="submit" loading={submitting} disabled={!canSubmit}>
              Salvar senha
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
